// Cognito auth for the ingest SPA (added 2026-06-16).
//
// Thin wrapper over amazon-cognito-identity-js: SRP sign-in, self sign-up (the pool's
// pre-sign-up trigger auto-confirms the two allowlisted emails, so sign-up logs in
// immediately), and an ID-token accessor used by api.ts. Tokens live in localStorage
// (library default) and auto-refresh via the 10-year refresh token, so a signed-in
// browser stays signed in ~indefinitely.
//
// The pool is constructed LAZILY so importing this module never throws when the
// VITE_COGNITO_* build vars are absent (AuthGate then shows a config message).

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";
import { COGNITO_USER_POOL_ID, COGNITO_CLIENT_ID } from "./config";

function configured(): boolean {
  return Boolean(COGNITO_USER_POOL_ID && COGNITO_CLIENT_ID);
}

let _pool: CognitoUserPool | null = null;
function pool(): CognitoUserPool {
  if (!_pool) {
    // Region is inferred from the pool id's region prefix (e.g. us-east-1_xxx).
    _pool = new CognitoUserPool({
      UserPoolId: COGNITO_USER_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    });
  }
  return _pool;
}

/** Resolve the current valid session (auto-refreshing tokens), or null. */
function getSession(): Promise<CognitoUserSession | null> {
  if (!configured()) return Promise.resolve(null);
  const user = pool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) resolve(null);
      else resolve(session);
    });
  });
}

// A single in-flight token request shared by concurrent callers, so a fan-out of
// API calls (or a token refresh) never triggers a refresh stampede.
let inflight: Promise<string | null> | null = null;

/** The current Cognito ID-token JWT (auto-refreshed), or null if not signed in. */
export function getIdToken(): Promise<string | null> {
  if (!configured()) return Promise.resolve(null);
  if (inflight) return inflight;
  inflight = getSession()
    .then((session) => (session ? session.getIdToken().getJwtToken() : null))
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Email of the signed-in user (from ID-token claims), or null. */
export async function currentUserEmail(): Promise<string | null> {
  const session = await getSession();
  if (!session) return null;
  const payload = session.getIdToken().decodePayload() as { email?: string };
  return payload.email ?? null;
}

/** SRP sign-in. Resolves on success; rejects with a user-facing Error. */
export function signIn(email: string, password: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise<void>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(normalizeAuthError(err)),
      // Auto-confirm means we should never hit this, but fail clearly if we do.
      newPasswordRequired: () =>
        reject(new Error("This account needs a password reset. Contact the administrator.")),
    });
  });
}

/**
 * Self sign-up, then sign in. The pool's pre-sign-up trigger rejects any email not
 * on the allowlist (the Error message is surfaced) and auto-confirms allowed ones,
 * so authentication succeeds immediately afterward.
 */
export function signUp(email: string, password: string): Promise<void> {
  const attrs = [new CognitoUserAttribute({ Name: "email", Value: email })];
  return new Promise<void>((resolve, reject) => {
    pool().signUp(email, password, attrs, [], (err) => {
      if (err) reject(normalizeAuthError(err));
      else resolve();
    });
  }).then(() => signIn(email, password));
}

/** Clear the cached session (localStorage tokens). */
export function signOut(): void {
  if (!configured()) return;
  pool().getCurrentUser()?.signOut();
}

// --- auth-expired pub/sub: api.ts signals a rejected token; AuthGate re-shows login -
type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe to "session is no longer valid" (returns an unsubscribe fn). */
export function onAuthExpired(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Called by api.ts on a 401: drop the bad session and notify AuthGate. */
export function notifyAuthExpired(): void {
  signOut();
  listeners.forEach((cb) => cb());
}

function normalizeAuthError(err: unknown): Error {
  const e = err as { message?: string } | undefined;
  let msg = e?.message?.trim() || "Authentication failed.";
  // Cognito wraps trigger errors: "PreSignUp failed with error <our message>."
  msg = msg.replace(/^PreSignUp failed with error\s*/i, "").trim();
  return new Error(msg);
}
